import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TrendData {
  date: string;
  overall_rating: number;
  accuracy_rating: number;
  satisfaction_rating: number;
  feedback_count: number;
}

interface FeedbackTrendsChartProps {
  trendsData: TrendData[];
  className?: string;
}

export const FeedbackTrendsChart: React.FC<FeedbackTrendsChartProps> = ({ 
  trendsData, 
  className = '' 
}) => {
  // Preparar dados para múltiplas linhas
  const labels = trendsData.map(d => {
    const date = new Date(d.date);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit'
    });
  });

  const data = {
    labels,
    datasets: [
      {
        label: 'Avaliação Geral',
        data: trendsData.map(d => d.overall_rating),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: 'Precisão',
        data: trendsData.map(d => d.accuracy_rating),
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#10B981',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: 'Satisfação',
        data: trendsData.map(d => d.satisfaction_rating),
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#F59E0B',
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Tendência de Feedbacks ao Longo do Tempo',
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#1F2937',
        padding: {
          bottom: 20,
        },
      },
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          font: {
            size: 12,
          },
          color: '#374151',
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          afterBody: function(context) {
            const dataIndex = context[0].dataIndex;
            const feedbackCount = trendsData[dataIndex]?.feedback_count || 0;
            return `\nTotal de feedbacks: ${feedbackCount}`;
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}/5.0`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          color: '#F3F4F6',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#6B7280',
          maxRotation: 45,
        },
      },
      y: {
        display: true,
        min: 1,
        max: 5,
        grid: {
          color: '#F3F4F6',
        },
        ticks: {
          font: {
            size: 12,
          },
          color: '#6B7280',
          stepSize: 0.5,
          callback: function(value) {
            return value + '/5';
          },
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart',
    },
  };

  if (trendsData.length === 0) {
    return (
      <div className={`flex items-center justify-center h-80 bg-gray-50 rounded-lg ${className}`}>
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-gray-500">Dados insuficientes para análise temporal</p>
          <p className="text-sm text-gray-400 mt-1">Necesário mais feedbacks ao longo do tempo</p>
        </div>
      </div>
    );
  }

  // Calcular estatísticas de tendência
  const latestData = trendsData[trendsData.length - 1];
  const previousData = trendsData.length > 1 ? trendsData[trendsData.length - 2] : null;
  
  const getTrend = (current: number, previous: number | null) => {
    if (!previous) return { direction: 'stable', change: 0 };
    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 2) return { direction: 'stable', change };
    return { direction: change > 0 ? 'up' : 'down', change };
  };

  const overallTrend = getTrend(latestData.overall_rating, previousData?.overall_rating || null);
  const accuracyTrend = getTrend(latestData.accuracy_rating, previousData?.accuracy_rating || null);
  const satisfactionTrend = getTrend(latestData.satisfaction_rating, previousData?.satisfaction_rating || null);

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <div className="h-80">
        <Line data={data} options={options} />
      </div>
      
      {/* Análise de tendências */}
      <div className="mt-4 grid grid-cols-3 gap-4 border-t pt-4">
        {[
          { label: 'Avaliação Geral', value: latestData.overall_rating, trend: overallTrend, color: 'blue' },
          { label: 'Precisão', value: latestData.accuracy_rating, trend: accuracyTrend, color: 'green' },
          { label: 'Satisfação', value: latestData.satisfaction_rating, trend: satisfactionTrend, color: 'yellow' },
        ].map(({ label, value, trend, color }) => (
          <div key={label} className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {value.toFixed(1)}/5.0
            </div>
            <div className="text-sm text-gray-500 mb-1">{label}</div>
            <div className={`flex items-center justify-center text-xs ${
              trend.direction === 'up' ? 'text-green-600' : 
              trend.direction === 'down' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {trend.direction === 'up' && (
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {trend.direction === 'down' && (
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {trend.direction === 'stable' && (
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {trend.direction === 'stable' ? 'Estável' : `${Math.abs(trend.change).toFixed(1)}%`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};